import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  Chip,
  Alert,
  Paper,
  LinearProgress,
  useTheme,
  Fade,
  Zoom,
  Grow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorageIcon from '@mui/icons-material/Storage';
import DevicesIcon from '@mui/icons-material/Devices';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { themeBackgrounds } from '../../theme/theme';
import { getCurrencySymbol } from '../../utils/currency';

interface CurrentPlanProps {
  onUpgradeClick?: () => void;
}

const CurrentPlan: React.FC<CurrentPlanProps> = ({ onUpgradeClick }) => {
  const theme = useTheme();
  const [expandedFeature, setExpandedFeature] = useState<string | false>('chat');
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const activePlanDetails = userDetails?.activePlanDetails;
  const planInfo = userDetails?.plan_details;

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFeature(isExpanded ? panel : false);
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper to render feature item
  const renderFeatureItem = (label: string, value: boolean | number | string, isEnabled?: boolean) => {
    if (typeof value === 'boolean') {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            py: 1,
            px: 2,
            borderRadius: 1,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 122, 255, 0.05)',
            },
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          {value ? (
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
          ) : (
            <CloseIcon sx={{ color: 'error.main', fontSize: 20 }} />
          )}
        </Box>
      );
    }

    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          py: 1,
          px: 2,
          borderRadius: 1,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 122, 255, 0.05)',
          },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Chip 
          label={value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      </Box>
    );
  };

  if (!activePlanDetails && !planInfo) {
    return (
      <Fade in timeout={800}>
        <Box sx={{ p: 3 }}>
          <Alert 
            severity="info"
            sx={{
              borderRadius: 3,
              '& .MuiAlert-icon': {
                fontSize: '2rem',
              },
            }}
          >
            You don't have an active plan yet. Please select a plan from the Plans tab to get started.
          </Alert>
        </Box>
      </Fade>
    );
  }

  const plan = activePlanDetails || {};
  const payment = planInfo || {};

  // Calculate usage percentages for demo (you can replace with real data)
  const storageUsed = 45; // percentage
  const audioMinutesUsed = 60; // percentage
  const videoMinutesUsed = 35; // percentage

  return (
    <Box sx={{ p: 3 }}>
      <Grid2 container spacing={3}>
        {/* Plan Overview Card - Hero Section */}
        <Grid2 size={{ xs: 12 }}>
          <Zoom in timeout={600}>
            <Paper
              elevation={8}
              sx={{
                p: 4,
                borderRadius: 3,
                background: themeBackgrounds.default,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                  opacity: 0.3,
                },
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    fontWeight="bold"
                    sx={{
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      fontSize: { xs: '1.75rem', md: '2.5rem' },
                    }}
                  >
                    {plan.plan_name || payment.plan_name || 'Current Plan'}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, opacity: 0.95, fontSize: '1.1rem', mb: 2 }}>
                    {plan.description || 'Your active subscription plan'}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<UpgradeIcon />}
                    onClick={onUpgradeClick}
                    sx={{
                      mt: 2,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Upgrade Plan
                  </Button>
                </Box>

                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box 
                        sx={{ 
                          p: 3,
                          height: 160,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                          },
                        }}
                      >
                        <AttachMoneyIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Amount
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {getCurrencySymbol(payment.currency)}{payment.amount.toLocaleString()}
                        </Typography>
                        {payment.billing_cycle && (
                          <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                            per {payment.billing_cycle}
                          </Typography>
                        )}
                      </Box>
                    </Grid2>

                  {payment.user_limit && (
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box 
                        sx={{ 
                          p: 3,
                          height: 160,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                          },
                        }}
                      >
                        <PeopleIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          User Tier
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {payment.user_limit}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5, height: '20px' }}>
                          {/* Placeholder for consistent height */}
                        </Typography>
                      </Box>
                    </Grid2>
                  )}

                  {payment.plan_start_date && (
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box 
                        sx={{ 
                          p: 3,
                          height: 160,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                          },
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Start Date
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                          {formatDate(payment.plan_start_date)}
                        </Typography>
                      </Box>
                    </Grid2>
                  )}

                  {plan.validity && (
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box 
                        sx={{ 
                          p: 3,
                          height: 160,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          borderRadius: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                          },
                        }}
                      >
                        <TrendingUpIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                        <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                          Validity
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {plan.validity.duration_days}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                          days
                        </Typography>
                      </Box>
                    </Grid2>
                  )}
                </Grid2>
              </Box>
            </Paper>
          </Zoom>
        </Grid2>

        {/* Features Card with Detailed Information */}
        <Grid2 size={{ xs: 12, lg: 8 }}>
          <Fade in timeout={800}>
            <Card 
              elevation={6}
              sx={{
                borderRadius: 3,
                transition: 'all 0.3s ease',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mb: 3 }}>
                  📋 Detailed Plan Features
                </Typography>

                {plan.features ? (
                  <Box>
                    {/* Chat Features Accordion */}
                    {plan.features.chat?.enabled && (
                      <Accordion 
                        expanded={expandedFeature === 'chat'} 
                        onChange={handleAccordionChange('chat')}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          '&:before': { display: 'none' },
                          boxShadow: expandedFeature === 'chat' ? theme.shadows[4] : theme.shadows[1],
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            borderRadius: expandedFeature === 'chat' ? '8px 8px 0 0' : 2,
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              color: 'white',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <ChatBubbleOutlineIcon />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                Chat Features
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                Unlimited messages
                                {/* {plan.features.chat.message_limit === -1 ? 'Unlimited' : plan.features.chat.message_limit.toLocaleString()} messages limit */}
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, backgroundColor: 'rgba(102, 126, 234, 0.02)' }}>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('File Sharing', plan.features.chat.file_sharing)}
                              {renderFeatureItem('Max File Size', `${plan.features.chat.max_file_size_mb} MB`)}
                              {renderFeatureItem('User Presence Indicator', plan.features.chat.user_presence_indicator)}
                              {renderFeatureItem('Typing Indicators', plan.features.chat.typing_indicators)}
                              {renderFeatureItem('Reaction on Messages', plan.features.chat.reaction_on_messages)}
                              {renderFeatureItem('Public Groups', plan.features.chat.public_groups)}
                              {renderFeatureItem('Delete Single Message', plan.features.chat.delete_single_message)}
                              {renderFeatureItem('Delete Conversation', plan.features.chat.delete_conversation)}
                              {renderFeatureItem('Read & Delivery Receipts', plan.features.chat.read_and_delivery_receipts)}
                              {renderFeatureItem('Mentions', plan.features.chat.mentions)}
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('Threaded Messages', plan.features.chat.threaded_messages)}
                              {renderFeatureItem('Quote Replies', plan.features.chat.quote_replies)}
                              {renderFeatureItem('Private & Protected Groups', plan.features.chat.private_and_protected_groups)}
                              {renderFeatureItem('Search in Messages', plan.features.chat.search_in_messages)}
                              {renderFeatureItem('Block Users', plan.features.chat.block_users)}
                              {renderFeatureItem('Mute Users', plan.features.chat.mute_users)}
                              {renderFeatureItem('Voice Audio Messages', plan.features.chat.voice_audio_messages)}
                              {renderFeatureItem('Pin Messages', plan.features.chat.pin_messages)}
                              {renderFeatureItem('Polls in Groups', plan.features.chat.polls_in_groups)}
                            </Grid2>
                          </Grid2>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Audio Call Features Accordion */}
                    {plan.features.audio_call?.enabled && (
                      <Accordion 
                        expanded={expandedFeature === 'audio'} 
                        onChange={handleAccordionChange('audio')}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          '&:before': { display: 'none' },
                          boxShadow: expandedFeature === 'audio' ? theme.shadows[4] : theme.shadows[1],
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
                            color: 'white',
                            borderRadius: expandedFeature === 'audio' ? '8px 8px 0 0' : 2,
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              color: 'white',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <PhoneIcon />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                Audio Call Features
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                {plan.features.audio_call.monthly_minutes === -1 ? 'Unlimited' : plan.features.audio_call.monthly_minutes.toLocaleString()} minutes/month • Up to {plan.features.audio_call.max_participants} participants
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, backgroundColor: 'rgba(0, 122, 255, 0.02)' }}>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('Recording Enabled', plan.features.audio_call.recording_enabled)}
                              {renderFeatureItem('One-to-One Call', plan.features.audio_call.one_to_one_call)}
                              {renderFeatureItem('Group Call', plan.features.audio_call.group_call)}
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('Invite Users', plan.features.audio_call.invite_users)}
                              {renderFeatureItem('Share Screen', plan.features.audio_call.share_screen)}
                              {renderFeatureItem('Remove Participants', plan.features.audio_call.remove_participants)}
                              {renderFeatureItem('Microphone Control', plan.features.audio_call.microphone_control)}
                            </Grid2>
                          </Grid2>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Video Call Features Accordion */}
                    {plan.features.video_call?.enabled && (
                      <Accordion 
                        expanded={expandedFeature === 'video'} 
                        onChange={handleAccordionChange('video')}
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          '&:before': { display: 'none' },
                          boxShadow: expandedFeature === 'video' ? theme.shadows[4] : theme.shadows[1],
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            color: 'white',
                            borderRadius: expandedFeature === 'video' ? '8px 8px 0 0' : 2,
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              color: 'white',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <VideocamIcon />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight="bold">
                                Video Call Features
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                {plan.features.video_call.monthly_minutes === -1 ? 'Unlimited' : plan.features.video_call.monthly_minutes.toLocaleString()} minutes/month • {plan.features.video_call.resolution_limit} quality
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, backgroundColor: 'rgba(118, 75, 162, 0.02)' }}>
                          <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('Max Participants', plan.features.video_call.max_participants)}
                              {renderFeatureItem('Recording Enabled', plan.features.video_call.recording_enabled)}
                              {renderFeatureItem('One-to-One Call', plan.features.video_call.one_to_one_call)}
                              {renderFeatureItem('Group Call', plan.features.video_call.group_call)}
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                              {renderFeatureItem('Invite Users', plan.features.video_call.invite_users)}
                              {renderFeatureItem('Share Screen', plan.features.video_call.share_screen)}
                              {renderFeatureItem('Remove Participants', plan.features.video_call.remove_participants)}
                              {renderFeatureItem('Microphone Control', plan.features.video_call.microphone_control)}
                            </Grid2>
                          </Grid2>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Storage & Limits Info */}
                    {(plan.limits?.storage_gb || plan.limits?.max_devices) && (
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                          color: 'white',
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                          💾 Storage & Device Limits
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                          {plan.limits.storage_gb && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StorageIcon sx={{ fontSize: 28 }} />
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  Storage
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {plan.limits.storage_gb === -1 ? 'Unlimited' : `${plan.limits.storage_gb} GB`}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {plan.limits.max_devices && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DevicesIcon sx={{ fontSize: 28 }} />
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  Max Devices
                                </Typography>
                                <Typography variant="h6" fontWeight="bold">
                                  {plan.limits.max_devices === -1 ? 'Unlimited' : plan.limits.max_devices}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    )}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Plan features information not available
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid2>

        {/* Usage Statistics Card */}
        <Grid2 size={{ xs: 12, lg: 4 }}>
          <Fade in timeout={900}>
            <Card 
              elevation={6}
              sx={{
                borderRadius: 3,
                position: { lg: 'sticky' },
                top: { lg: 24 },
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: theme.shadows[12],
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mb: 3 }}>
                  📊 Usage Statistics
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      <StorageIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1 }} />
                      Storage Usage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {storageUsed}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={storageUsed} 
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'rgba(0, 122, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background: 'linear-gradient(90deg, #5fb0ff 0%, #007aff 100%)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      <PhoneIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1 }} />
                      Audio Minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {audioMinutesUsed}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={audioMinutesUsed} 
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'rgba(0, 122, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background: 'linear-gradient(90deg, #007aff 0%, #5fb0ff 100%)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      <VideocamIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 1 }} />
                      Video Minutes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {videoMinutesUsed}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={videoMinutesUsed} 
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: 'rgba(0, 122, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                        background: 'linear-gradient(90deg, #764ba2 0%, #667eea 100%)',
                      },
                    }}
                  />
                </Box>

                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    💡 Your resources are being utilized efficiently
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid2>

        {/* Overage Charges Card */}
        {plan.overage_charges && (
          <Grid2 size={{ xs: 12 }}>
            <Zoom in timeout={1000}>
              <Card 
                elevation={6}
                sx={{
                  borderRadius: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: theme.shadows[16],
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h3" fontWeight="bold" sx={{ mb: 3 }}>
                    💰 Overage Charges
                  </Typography>
                  <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12, sm: 3 }}>
                      <Grow in timeout={1200}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #007aff 0%, #5fb0ff 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05) translateY(-8px)',
                              boxShadow: '0 12px 24px rgba(0, 122, 255, 0.4)',
                            },
                          }}
                        >
                          <PhoneIcon sx={{ fontSize: 50, mb: 2, opacity: 0.9 }} />
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.95 }}>
                            Audio Per Minute
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {getCurrencySymbol(payment.currency)}{plan.overage_charges.audio_per_minute}
                          </Typography>
                        </Paper>
                      </Grow>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 3 }}>
                      <Grow in timeout={1400}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05) translateY(-8px)',
                              boxShadow: '0 12px 24px rgba(118, 75, 162, 0.4)',
                            },
                          }}
                        >
                          <VideocamIcon sx={{ fontSize: 50, mb: 2, opacity: 0.9 }} />
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.95 }}>
                            Video Per Minute
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {getCurrencySymbol(payment.currency)}{plan.overage_charges.video_per_minute}
                          </Typography>
                        </Paper>
                      </Grow>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 3 }}>
                      <Grow in timeout={1600}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05) translateY(-8px)',
                              boxShadow: '0 12px 24px rgba(95, 176, 255, 0.4)',
                            },
                          }}
                        >
                          <StorageIcon sx={{ fontSize: 50, mb: 2, opacity: 0.9 }} />
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.95 }}>
                            Extra Storage Per GB
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {getCurrencySymbol(payment.currency)}{plan.overage_charges.extra_storage_per_gb}
                          </Typography>
                        </Paper>
                      </Grow>
                    </Grid2>
                     <Grid2 size={{ xs: 12, sm: 3 }}>
                      <Grow in timeout={1600}>
                        <Paper 
                          elevation={3} 
                          sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #5fb0ff 0%, #007aff 100%)',
                            color: 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05) translateY(-8px)',
                              boxShadow: '0 12px 24px rgba(95, 176, 255, 0.4)',
                            },
                          }}
                        >
                          <StorageIcon sx={{ fontSize: 50, mb: 2, opacity: 0.9 }} />
                          <Typography variant="body2" sx={{ mb: 1, opacity: 0.95 }}>
                            Extra Charges Per Extra User
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {getCurrencySymbol(payment.currency)}{plan.overage_charges.per_extra_user}
                          </Typography>
                        </Paper>
                      </Grow>
                    </Grid2>
                  </Grid2>
                </CardContent>
              </Card>
            </Zoom>
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
};

export default CurrentPlan;
